import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { navigateTo } from '../router';

// WhyPage is SaaS-only (route-gated), so the hosted MCP endpoint is correct here.
const MCP_URL = 'https://mcp.llmcontext.dev';

const MCP_CONFIG = `{
  "mcpServers": {
    "llmcontext": {
      "url": "${MCP_URL}"
    }
  }
}`;

const CLOSING_TEXT =
  "In my own experimentation, a little pre-work — organizing what exists and handing the model the right context up front — makes it improve so much that it finally feels like AI again, instead of a very fast autocomplete. I'm not claiming this solves it all. But if any of this sounds like your week, maybe give it a shot. I'm also open to suggestions on what would make it more useful to you.";

function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-bg px-1.5 py-0.5 font-mono text-[0.9em] text-link">{children}</code>
  );
}

function CodeBlock({
  title,
  code,
  lang,
  onCopy,
  copied,
}: {
  title?: string;
  code: string;
  lang: string;
  onCopy?: () => void;
  copied?: boolean;
}) {
  return (
    <div className="overflow-hidden border border-rule">
      {title && (
        <div className="flex items-center justify-between border-b border-rule bg-bg px-4 py-2">
          <span className="text-xs font-medium text-dim">{title}</span>
          <div className="flex items-center gap-3">
            <span className="text-3xs uppercase tracking-wider text-dim">{lang}</span>
            {onCopy && (
              <button
                onClick={onCopy}
                className="text-2xs text-dim transition-colors hover:text-ink"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>
        </div>
      )}
      <pre className="overflow-x-auto bg-code-bg p-4 text-xs leading-relaxed text-ink">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Caret() {
  return (
    <span
      aria-hidden
      className="ml-0.5 inline-block h-[0.95em] w-[2px] translate-y-[0.15em] animate-pulse bg-accent align-baseline"
    />
  );
}

function useTypewriter(text: string, speed: number, enabled: boolean) {
  const [out, setOut] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) {
        window.clearInterval(id);
        setDone(true);
      }
    }, speed);
    return () => window.clearInterval(id);
  }, [text, speed, enabled]);
  return { out, done };
}

export function WhyPage() {
  const [copied, setCopied] = useState(false);
  const copyMcp = () => {
    void navigator.clipboard.writeText(MCP_CONFIG);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const bullets: React.ReactNode[] = [
    <>
      The agent would run a dozen <InlineCode>grep</InlineCode>s and <InlineCode>cat</InlineCode>s
      into <InlineCode>.venv</InlineCode> or <InlineCode>node_modules</InlineCode> just to figure
      out what a library does — and still get it wrong when the library was new, or had just shipped
      a big rewrite.
    </>,
    <>
      The agent would only look at one small corner of the project and then write code that ignored
      the rest — duplicating what already existed, breaking patterns I'd already picked, and quietly
      messing up the codebase because it never saw the whole picture.
    </>,
    <>
      The agent would reach for deprecated packages, miss better options that already existed, and
      pile on tech debt one suggestion at a time. The same mistakes would show up again the next
      session, because nothing in the loop actually knew what was out there.
    </>,
    <>
      Agents were fine for prototypes. On real projects — where internal libraries sit next to
      public ones — even a simple microservice became tedious, because the agent couldn't see both
      sides at once. I still wanted the agent; I just needed it to know what I was working with.
    </>,
  ];

  const [bulletIdx, setBulletIdx] = useState(0);
  const bulletsDone = bulletIdx >= bullets.length;
  const closing = useTypewriter(CLOSING_TEXT, 12, bulletsDone);

  useEffect(() => {
    if (bulletIdx >= bullets.length) return;
    const delay = bulletIdx === 0 ? 350 : 500;
    const t = window.setTimeout(() => setBulletIdx((n) => n + 1), delay);
    return () => window.clearTimeout(t);
  }, [bulletIdx, bullets.length]);

  return (
    <div className="mx-auto max-w-3xl space-y-14 px-2 py-14 font-mono">
      <style>{`
        @keyframes wp-fade-up {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .wp-fade-up { animation: wp-fade-up 320ms ease-out both; }
      `}</style>

      <section className="space-y-6">
        <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          llmcontext<span className="text-accent">.dev</span>
        </h1>

        <ul className="space-y-4 text-base leading-relaxed text-link">
          {bullets.slice(0, bulletIdx).map((content, i) => (
            <li key={i} className="wp-fade-up flex gap-3">
              <span className="mt-[0.6rem] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
              <span>
                {content}
                {i === bulletIdx - 1 && bulletIdx < bullets.length && <Caret />}
              </span>
            </li>
          ))}
        </ul>

        {bulletsDone && (
          <p className="wp-fade-up text-base leading-relaxed text-link">
            {closing.out}
            {!closing.done && <Caret />}
          </p>
        )}
      </section>

      {closing.done && (
        <section className="wp-fade-up space-y-5 border-t border-rule pt-12">
          <div>
            <h2 className="text-xl font-semibold text-ink">Want to try it?</h2>
            <p className="mt-2 text-sm text-dim">
              Add it to your MCP client. Your agent gets two tools:{' '}
              <InlineCode>search_packages</InlineCode> to discover indexed packages and versions,
              and <InlineCode>get_context</InlineCode> to ask one focused question and get a
              grounded answer from indexed source.
            </p>
          </div>
          <CodeBlock
            title="mcp.json"
            lang="json"
            code={MCP_CONFIG}
            onCopy={copyMcp}
            copied={copied}
          />
          <p className="text-xs text-dim">
            Public packages need no API key. Add an{' '}
            <InlineCode>Authorization: Bearer &lt;api_key&gt;</InlineCode> header only for private
            org or workspace-scoped access.
          </p>
          <div className="flex flex-wrap gap-3 pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigateTo('/search')}
              className="gap-1.5"
            >
              Browse libraries
              <svg
                className="h-4 w-4 text-dim"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigateTo('/docs')}
              className="gap-1.5"
            >
              Get started
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
